var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Children, cloneElement, useEffect, useRef, useState, } from "react";
import classNames from "classnames";
import { CarouselItem, CarouselPagination } from "./components";
import CarouselButton from "./CarouselButton";
import { groupArray } from "./utils/groupArray";
import { calculateStyle } from "./utils/calculateStyle";
// styles
import "./carousel.scss";
// use the optional prop interface to define the default props
const defaultProps = {
    "data-testid": "tecma-carousel",
    style: {},
    children: [],
    className: "",
    showPagination: true,
    slideClassName: "",
    sliderClassName: "",
    slidesPerView: 1,
    centeredSlides: false,
    width: "100%",
    selectedItemIndex: 0,
    onChange: () => { },
    loop: true,
};
const SWIPE_PIXEL_THRESHOLD = 5;
const Carousel = (_a) => {
    var { className, children, buttons, style, showPagination, slidesPerView, centeredSlides, slideClassName, sliderClassName, width, selectedItemIndex, onChange, loop, swipe = {
        prev: () => { },
        next: () => { },
    }, slidesGap } = _a, rest = __rest(_a, ["className", "children", "buttons", "style", "showPagination", "slidesPerView", "centeredSlides", "slideClassName", "sliderClassName", "width", "selectedItemIndex", "onChange", "loop", "swipe", "slidesGap"]);
    const { prev, next } = swipe;
    const carouselWrapperRef = useRef(null);
    const [touchPosition, setTouchPosition] = useState(null);
    /*
     * create a copy of component children in order to manipulate the array without touching the incoming props
     * the array manipulation is required when the carousel show more than a slide per time
     */
    const nextChildren = children === null || children === void 0 ? void 0 : children.map((i) => cloneElement(i));
    /**
     * The groupChildrenArray return children as is if slidesPerView === 1
     * else it will group the slides based on slidesPerView value
     */
    const groupChildrenArray = groupArray(nextChildren, slidesPerView);
    useEffect(() => {
        if (onChange) {
            onChange(selectedItemIndex, Children.toArray(children)[selectedItemIndex]);
        }
    }, [centeredSlides, children, onChange, selectedItemIndex, slidesPerView]);
    const changeItem = (newIndex) => () => {
        if (onChange) {
            onChange(newIndex, Children.toArray(children)[newIndex]);
        }
    };
    const handleTouchStart = (e) => {
        const touchDown = e.touches[0].clientX;
        setTouchPosition(touchDown);
    };
    const handleTouchMove = (e) => {
        const touchDown = touchPosition;
        if (touchDown === null) {
            return;
        }
        const currentTouch = e.touches[0].clientX;
        const diff = touchDown - currentTouch;
        if (diff > SWIPE_PIXEL_THRESHOLD && next) {
            next();
        }
        if (diff < -SWIPE_PIXEL_THRESHOLD && prev) {
            prev();
        }
        setTouchPosition(null);
    };
    const classContainerList = classNames("tecma-carousel", className);
    const classSliderList = classNames("slider", sliderClassName);
    const wrapperClassList = classNames("slider-wrapper", { centered: centeredSlides, loop });
    return (_jsxs("div", Object.assign({ className: classContainerList, ref: carouselWrapperRef }, rest, { style: Object.assign({}, style), onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, children: [_jsx("div", { className: wrapperClassList, children: _jsx("ul", { className: classSliderList, children: groupChildrenArray &&
                        groupChildrenArray.map((item, index) => (_jsx(CarouselItem
                        // eslint-disable-next-line react/no-array-index-key
                        , { className: slideClassName, selected: index === selectedItemIndex, previous: selectedItemIndex === groupChildrenArray.length - 1
                                ? index !== 0 && index === selectedItemIndex - 1
                                : index === selectedItemIndex - 1, next: selectedItemIndex === groupChildrenArray.length - 1
                                ? index === 0
                                : index === selectedItemIndex + 1, style: Object.assign({}, calculateStyle(index, selectedItemIndex, loop, centeredSlides, children === null || children === void 0 ? void 0 : children.length, slidesGap)), children: Children.map(item, (child) => child) }, `itemKey${index}`))) }) }), _jsx("div", { className: "carousel-buttons-container", children: buttons }), showPagination && (_jsx(CarouselPagination, { itemsNumber: groupChildrenArray.length, onClick: changeItem, selectedItemIndex: selectedItemIndex }))] })));
};
Carousel.defaultProps = defaultProps;
const CarouselExport = Object.assign(React.memo(Carousel), {
    Button: CarouselButton,
});
export default CarouselExport;
